<script setup lang="ts">
import { computed } from 'vue';
import { RouterView, useRoute } from 'vue-router';
import { TooltipProvider } from '@hq/chat-ui';
import { useAuthStore } from '@/stores/auth';
import AppShell from '@/components/AppShell.vue';

const auth = useAuthStore();
const route = useRoute();

// Routes that render full-bleed (no shell) - the unauthenticated /
// pre-session surfaces (sign-in, magic-link landing, workspace signup, the
// Slack install/invite landing, OAuth callbacks) and the 404. These must NOT
// show the app side-nav: the visitor often has no session yet (or, for the
// install landing, is adding a DIFFERENT workspace than the one they're signed
// into), so the shell would be empty, wrong, or the wrong tenant's.
const FULL_BLEED = new Set([
  'signin', 'magic-link', 'signup-workspace', 'get-started-slack',
  // The onboarding wizard is a hard gate for a brand-new tenant: render it
  // standalone (no app nav to roam into) with its own switch / sign-out escape.
  'onboarding',
  'oauth-callback', 'oauth-consent', 'oauth-device', 'not-found',
]);
const isFullBleed = computed(() => FULL_BLEED.has(String(route.name ?? '')));
</script>

<template>
  <!-- One TooltipProvider at the root drives every <Tooltip>: the 350ms open
       delay matches the old directive, and skip-delay means moving between
       adjacent tooltips shows the next instantly (a 300ms grace window). -->
  <TooltipProvider :delay-duration="350" :skip-delay-duration="300">
    <RouterView v-if="isFullBleed" />
    <AppShell v-else :user="auth.user">
      <RouterView />
    </AppShell>
  </TooltipProvider>
</template>
